import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";

/** Why a leader being replaced may not stay on as a member: a leader is exempt from the credentials a member needs (#5800). */
export const FORMER_LEADER_LACKS_CREDENTIALS_REASON = "their profile lists no professional credentials, which a member needs";

const STAYS = "stays";
const LEAVES = "leaves";

type FormerLeaderChoiceProps = {
  chosenLeaderName: string;
  formerLeaderName?: string;
  /** Whether the leader being replaced is known to lack the credentials a member needs. */
  formerLeaderLacksCredentials: boolean;
  /** Whether they stay on as a member; undefined until somebody has chosen. */
  stays?: boolean;
  onChange: (stays: boolean) => void;
};

/**
 * What becomes of the leader the picker is replacing (#6602). Asked, never assumed: keeping them on
 * as a member holds them to the credentials a member needs, which a leader is not held to, so a
 * roster that kept them silently could be one the server refuses to save. When they cannot stay,
 * the only answer left is to remove them, and the picker waits for it to be chosen.
 */
export default function FormerLeaderChoice(props: Readonly<FormerLeaderChoiceProps>) {
  const former = props.formerLeaderName ?? "The current leader";
  let value = "";
  if (props.stays === true) value = STAYS;
  if (props.stays === false) value = LEAVES;
  return (
    <FormControl component="fieldset" size="small">
      <FormLabel component="legend" sx={{ typography: "body2" }}>
        {`${props.chosenLeaderName} will replace ${former} as leader.`}
      </FormLabel>
      <RadioGroup row value={value} onChange={(event) => props.onChange(event.target.value === STAYS)}>
        <FormControlLabel
          value={STAYS}
          disabled={props.formerLeaderLacksCredentials}
          control={<Radio size="small" />}
          label={`Keep ${former} as a member`}
        />
        <FormControlLabel value={LEAVES} control={<Radio size="small" />} label={`Remove ${former} from the team`} />
      </RadioGroup>
      {props.formerLeaderLacksCredentials && (
        <FormHelperText sx={{ mx: 0 }}>{`${former} can't stay on as a member: ${FORMER_LEADER_LACKS_CREDENTIALS_REASON}.`}</FormHelperText>
      )}
    </FormControl>
  );
}
